import { type ReactNode } from 'react';
import { cn } from '@/utils/cn';
import { HelpTip } from '@/components/ui/help-tip';
import { useCountdown } from '@/hooks/use-countdown';
import type { ChipData, Tone } from '../utils/bonus-indicators';
import { HeroBlock, ChipProgressBar } from './bonus-chip-parts';

const STATUS_TONE: Record<Tone, string> = {
  success: 'text-success',
  warning: 'text-error',
  muted: 'text-text-tertiary',
};

type HeaderProps = {
  title: string;
  subtitle?: string;
  countdownTo?: string;
  tooltip: string;
  headerRight?: ReactNode;
};

function useHeaderSubtitle(props: HeaderProps): string | undefined {
  const cd = useCountdown(props.countdownTo);
  if (cd && !cd.expired) return `Se cierra en ${cd.label}`;
  return props.subtitle;
}

function HeaderText(props: HeaderProps): ReactNode {
  const subtitle = useHeaderSubtitle(props);
  return (
    <p className="text-[10px] font-medium uppercase tracking-wider text-text-tertiary/60">
      {props.title}
      {props.tooltip && (
        <span className="ml-0.5 inline-block normal-case">
          <HelpTip text={props.tooltip} />
        </span>
      )}
      {subtitle && (
        <span className="ml-1 normal-case text-text-tertiary/40">
          · {subtitle}
        </span>
      )}
    </p>
  );
}

function ChipHeader(props: HeaderProps): ReactNode {
  return (
    <div className="flex items-start justify-between gap-sm">
      <HeaderText {...props} />
      {props.headerRight}
    </div>
  );
}

function ChipStatus({
  status,
  tone,
}: {
  status: string;
  tone: Tone;
}): ReactNode {
  return (
    <p className={cn('text-xs font-medium leading-snug', STATUS_TONE[tone])}>
      {status}
    </p>
  );
}

type ViewProps = { chip: ChipData; headerRight?: ReactNode };

export function BonusChipView({ chip, headerRight }: ViewProps): ReactNode {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-white/[0.04] bg-white/[0.02] p-base">
      <ChipHeader
        title={chip.title}
        tooltip={chip.tooltip}
        {...(chip.subtitle && { subtitle: chip.subtitle })}
        {...(chip.subtitleCountdownTo && {
          countdownTo: chip.subtitleCountdownTo,
        })}
        {...(headerRight && { headerRight })}
      />
      <HeroBlock hero={chip.hero} tone={chip.tone} />
      {chip.status && <ChipStatus status={chip.status} tone={chip.tone} />}
      {chip.progress && (
        <ChipProgressBar progress={chip.progress} tone={chip.tone} />
      )}
      {chip.hint && (
        <p className="text-[11px] leading-snug text-text-tertiary/80">
          {chip.hint}
        </p>
      )}
    </div>
  );
}
